/**
 * Central Workflow Engine
 * 
 * Single source of truth for all workflow stages, transitions,
 * prerequisites, ownership, and gating logic.
 * 
 * IMPORTANT: This is an additive infrastructure layer.
 * Existing behavior in useWorkflowOrchestration and individual
 * components is preserved — this config will progressively
 * replace hardcoded logic over time.
 */

import type { Job, JobStage } from "./types";
import type { UserRole } from "./RoleContext";

/* ═══════════════════════════════════════════
   STAGE DEFINITIONS
   ═══════════════════════════════════════════ */

export interface WorkflowStageConfig {
  /** Unique stage key — matches JobStage type */
  id: JobStage;
  /** Human-readable label */
  label: string;
  /** Display order (ascending) */
  order: number;
  /** Owning module key from ModuleRegistry */
  moduleId: string;
  /** Whether this stage is currently active in the workflow */
  enabled: boolean;
  /** Can this stage be skipped (with audit trail)? */
  skippable: boolean;
  /** Roles allowed to act on jobs in this stage */
  actorRoles: UserRole[];
  /** Roles that can view jobs in this stage */
  viewerRoles: UserRole[];
  /** Prerequisite checks that must pass to enter this stage */
  prerequisites: StagePrerequisite[];
  /** Stages that can follow this one */
  allowedNextStages: JobStage[];
  /** Status fields this stage owns / is allowed to mutate */
  ownedStatuses: StatusFieldKey[];
  /** Optional description for documentation */
  description?: string;
}

export interface StagePrerequisite {
  id: string;
  label: string;
  /** Field path on Job to check */
  field: string;
  /** Check function — returns true if prerequisite is met */
  check: (job: Job) => boolean;
  /** If true, this is a hard block. If false, it's a warning. */
  blocking: boolean;
}

/** All status fields that can be owned by a stage */
export type StatusFieldKey =
  | "currentStage"
  | "planningStatus"
  | "orderingStatus"
  | "materialReadiness"
  | "schedulingStatus"
  | "dispatchStatus"
  | "installStatus"
  | "qaStatus"
  | "closeoutStatus"
  | "rescissionStatus"
  | "holdStatus";

/* ═══════════════════════════════════════════
   ADMIN / ALL ROLES shorthand
   ═══════════════════════════════════════════ */

const ADMIN_ROLES: UserRole[] = ["super_admin", "owner", "admin", "ops_manager"];
const OPS_ROLES: UserRole[] = [...ADMIN_ROLES, "procurement_manager", "warehouse_manager", "head_of_installation"];
const ALL_ROLES: UserRole[] = [
  "super_admin", "owner", "ops_manager", "admin",
  "warehouse_manager", "installer", "subcontractor",
  "sales", "cfo", "finance_manager", "bookkeeper",
  "head_of_installation", "procurement_manager", "inventory_manager",
];

/* ═══════════════════════════════════════════
   STAGE REGISTRY
   ═══════════════════════════════════════════ */

export const WORKFLOW_STAGES: WorkflowStageConfig[] = [
  {
    id: "Sale Closed",
    label: "Sale Closed",
    order: 10,
    moduleId: "sales",
    enabled: true,
    skippable: false,
    actorRoles: ["sales", ...ADMIN_ROLES],
    viewerRoles: ALL_ROLES,
    prerequisites: [],
    allowedNextStages: ["Review and Validation"],
    ownedStatuses: ["currentStage"],
    description: "Contract signed, job record auto-created from opportunity.",
  },
  {
    id: "Review and Validation",
    label: "Build Review",
    order: 20,
    moduleId: "build_review",
    enabled: true,
    skippable: false,
    actorRoles: ADMIN_ROLES,
    viewerRoles: OPS_ROLES,
    prerequisites: [
      {
        id: "pre-review-contract",
        label: "Contract must be signed",
        field: "contractSigned",
        check: (job) => !!job.intakeApproved || !!job.approvedForOrdering,
        blocking: true,
      },
    ],
    allowedNextStages: ["Review Complete"],
    ownedStatuses: ["currentStage", "holdStatus"],
    description: "Operations reviews scope, verifies build data, resolves holds.",
  },
  {
    id: "Review Complete",
    label: "Review Complete",
    order: 25,
    moduleId: "build_review",
    enabled: true,
    skippable: false,
    actorRoles: ADMIN_ROLES,
    viewerRoles: OPS_ROLES,
    prerequisites: [
      {
        id: "pre-review-approved",
        label: "Build review must be approved",
        field: "buildApproved",
        check: (job) => job.intakeApproved === true || job.approvedForOrdering === true,
        blocking: true,
      },
      {
        id: "pre-review-home-type",
        label: "Home type must be set for permit application",
        field: "homeType",
        check: (job) => !!job.homeType,
        blocking: true,
      },
      {
        id: "pre-review-parcel-number",
        label: "Parcel number must be set for permit application",
        field: "parcelNumber",
        check: (job) => !!job.parcelNumber,
        blocking: true,
      },
    ],
    allowedNextStages: ["Ready for Ordering"],
    ownedStatuses: ["currentStage", "planningStatus"],
    description: "Build review approved, job enters planning pipeline.",
  },
  {
    id: "Ready for Ordering",
    label: "Ready for Ordering",
    order: 30,
    moduleId: "ordering",
    enabled: true,
    skippable: false,
    actorRoles: [...ADMIN_ROLES, "procurement_manager"],
    viewerRoles: OPS_ROLES,
    prerequisites: [
      {
        id: "pre-ordering-approved",
        label: "Build review must be approved",
        field: "approvedForOrdering",
        check: (job) => job.approvedForOrdering === true,
        blocking: true,
      },
    ],
    allowedNextStages: ["Ordered"],
    ownedStatuses: ["currentStage", "planningStatus", "orderingStatus"],
    description: "Job is queued for material procurement.",
  },
  {
    id: "Ordered",
    label: "Materials Ordered",
    order: 40,
    moduleId: "ordering",
    enabled: true,
    skippable: false,
    actorRoles: [...ADMIN_ROLES, "procurement_manager"],
    viewerRoles: OPS_ROLES,
    prerequisites: [],
    allowedNextStages: ["Permit and Order Pending", "Ready for Scheduling"],
    ownedStatuses: ["currentStage", "planningStatus"],
    description: "POs submitted to vendors.",
  },
  {
    id: "Permit and Order Pending",
    label: "Permit & Order Pending",
    order: 45,
    moduleId: "ordering",
    enabled: true,
    skippable: true,
    actorRoles: OPS_ROLES,
    viewerRoles: OPS_ROLES,
    prerequisites: [
      {
        id: "pre-permit-drawing-completed",
        label: "Floor plan must be completed for permit submission",
        field: "drawingFloorPlanCompleted",
        check: (job) => job.drawingFloorPlanCompleted === true,
        blocking: true,
      },
    ],
    allowedNextStages: ["Ready for Scheduling"],
    ownedStatuses: ["currentStage"],
    description: "Awaiting permit approval and/or material delivery.",
  },
  {
    id: "Ready for Scheduling",
    label: "Ready for Scheduling",
    order: 50,
    moduleId: "install_readiness",
    enabled: true,
    skippable: false,
    actorRoles: OPS_ROLES,
    viewerRoles: OPS_ROLES,
    prerequisites: [
      {
        id: "pre-sched-materials",
        label: "Materials must be received or staged",
        field: "materialReadiness",
        check: (job) =>
          job.materialReadiness === "Fully Received" ||
          job.materialReadiness === "Staged for Job" ||
          job.materialReadiness === "Loaded for Crew",
        blocking: true,
      },
    ],
    allowedNextStages: ["Scheduled Tentative", "Scheduled Confirmed"],
    ownedStatuses: ["currentStage", "schedulingStatus"],
    description: "All prerequisites met — job can be scheduled.",
  },
  {
    id: "Scheduled Tentative",
    label: "Scheduled (Tentative)",
    order: 55,
    moduleId: "scheduling",
    enabled: true,
    skippable: true,
    actorRoles: [...ADMIN_ROLES, "head_of_installation"],
    viewerRoles: OPS_ROLES,
    prerequisites: [],
    allowedNextStages: ["Scheduled Confirmed", "Ready for Dispatch"],
    ownedStatuses: ["currentStage", "schedulingStatus"],
    description: "Date set but not confirmed with crew.",
  },
  {
    id: "Scheduled Confirmed",
    label: "Scheduled (Confirmed)",
    order: 60,
    moduleId: "scheduling",
    enabled: true,
    skippable: false,
    actorRoles: [...ADMIN_ROLES, "head_of_installation"],
    viewerRoles: OPS_ROLES,
    prerequisites: [],
    allowedNextStages: ["Ready for Dispatch"],
    ownedStatuses: ["currentStage", "schedulingStatus"],
    description: "Date confirmed with crew and customer.",
  },
  {
    id: "Ready for Dispatch",
    label: "Ready for Dispatch",
    order: 65,
    moduleId: "scheduling",
    enabled: true,
    skippable: false,
    actorRoles: OPS_ROLES,
    viewerRoles: OPS_ROLES,
    prerequisites: [
      {
        id: "pre-dispatch-ready-for-inspection",
        label: "Job must be ready for inspection before dispatch",
        field: "readyForInspection",
        check: (job) => job.readyForInspection === true,
        blocking: true,
      },
    ],
    allowedNextStages: ["Available for Contractor Assignment", "Assigned and Dispatched"],
    ownedStatuses: ["currentStage", "dispatchStatus"],
    description: "Job confirmed and ready for crew dispatch.",
  },
  {
    id: "Available for Contractor Assignment",
    label: "Available for Assignment",
    order: 68,
    moduleId: "scheduling",
    enabled: true,
    skippable: true,
    actorRoles: OPS_ROLES,
    viewerRoles: [...OPS_ROLES, "subcontractor"],
    prerequisites: [],
    allowedNextStages: ["Assigned and Dispatched"],
    ownedStatuses: ["currentStage", "dispatchStatus"],
    description: "Job posted for contractor bidding.",
  },
  {
    id: "Assigned and Dispatched",
    label: "Assigned & Dispatched",
    order: 70,
    moduleId: "scheduling",
    enabled: true,
    skippable: false,
    actorRoles: OPS_ROLES,
    viewerRoles: [...OPS_ROLES, "installer", "subcontractor"],
    prerequisites: [
      {
        id: "offer_accepted",
        label: "Installer offer must be accepted before dispatch",
        field: "contractorAssignmentStatus",
        check: (job: Job) => job.contractorAssignmentStatus === "Accepted",
        blocking: true,
      },
    ],
    allowedNextStages: ["In Progress"],
    ownedStatuses: ["currentStage", "dispatchStatus"],
    description: "Crew assigned and dispatched to job site.",
  },
  {
    id: "In Progress",
    label: "In Progress",
    order: 80,
    moduleId: "installer",
    enabled: true,
    skippable: false,
    actorRoles: ["installer", "subcontractor", ...ADMIN_ROLES],
    viewerRoles: ALL_ROLES,
    prerequisites: [],
    allowedNextStages: ["QA Review"],
    ownedStatuses: ["currentStage", "installStatus"],
    description: "Active installation work underway.",
  },
  {
    id: "QA Review",
    label: "QA Review",
    order: 90,
    moduleId: "qa_closeout",
    enabled: true,
    skippable: false,
    actorRoles: ADMIN_ROLES,
    viewerRoles: OPS_ROLES,
    prerequisites: [
      {
        id: "pre-qa-submitted",
        label: "QA must be submitted by installer",
        field: "qaSubmitted",
        check: (job) => job.qaSubmitted === true,
        blocking: true,
      },
    ],
    allowedNextStages: ["Completed"],
    ownedStatuses: ["currentStage", "qaStatus", "closeoutStatus"],
    description: "Post-install quality inspection and closeout review.",
  },
  {
    id: "Completed",
    label: "Completed",
    order: 100,
    moduleId: "qa_closeout",
    enabled: true,
    skippable: false,
    actorRoles: ADMIN_ROLES,
    viewerRoles: ALL_ROLES,
    prerequisites: [],
    allowedNextStages: [],
    ownedStatuses: ["currentStage", "closeoutStatus"],
    description: "Job fully closed, review/referral flywheel triggered.",
  },
  {
    id: "Issue",
    label: "Issue",
    order: 999,
    moduleId: "qa_closeout",
    enabled: true,
    skippable: false,
    actorRoles: ADMIN_ROLES,
    viewerRoles: OPS_ROLES,
    prerequisites: [],
    allowedNextStages: ["In Progress", "QA Review"],
    ownedStatuses: ["currentStage"],
    description: "Job flagged with blocking issue requiring resolution.",
  },
];

/* ═══════════════════════════════════════════
   STATUS OWNERSHIP MAP
   ═══════════════════════════════════════════ */

/**
 * Defines which module owns each status field.
 * Only the owning module should write to these fields.
 */
export const STATUS_OWNERSHIP: Record<StatusFieldKey, {
  ownerModule: string;
  description: string;
  allowedValues: string[];
}> = {
  currentStage: {
    ownerModule: "workflow_engine",
    description: "Primary lifecycle stage — controlled by workflow transitions",
    allowedValues: [
      "Sale Closed", "Review and Validation", "Review Complete",
      "Ready for Ordering", "Ordered", "Permit and Order Pending",
      "Ready for Scheduling", "Scheduled Tentative", "Scheduled Confirmed",
      "Ready for Dispatch", "Available for Contractor Assignment",
      "Assigned and Dispatched", "In Progress", "QA Review", "Completed", "Issue",
    ],
  },
  planningStatus: {
    ownerModule: "ordering",
    description: "Procurement workflow progress",
    allowedValues: [
      "Not Started", "In Planning", "Ready for Ordering",
      "Ordered", "Awaiting Delivery", "Receiving", "Staged", "Ready for Install",
    ],
  },
  orderingStatus: {
    ownerModule: "ordering",
    description: "Vendor-level order tracking",
    allowedValues: ["Not Ordered", "Ordered", "Confirmed", "Delivered"],
  },
  materialReadiness: {
    ownerModule: "warehouse",
    description: "Physical material availability for the job",
    allowedValues: [
      "Not Ordered", "Ordered", "Partially Received", "Fully Received",
      "Staged for Job", "Loaded for Crew", "Missing Items",
      "Field Purchase Required",
    ],
  },
  schedulingStatus: {
    ownerModule: "scheduling",
    description: "Scheduling lifecycle position",
    allowedValues: ["Not Ready", "Unscheduled", "Scheduled", "Crew Assigned", "Confirmed"],
  },
  dispatchStatus: {
    ownerModule: "scheduling",
    description: "Day-of dispatch state",
    allowedValues: [
      "Not Ready", "Ready for Dispatch",
      "Sent to Contractor Network", "Awaiting Response",
      "Assigned", "Accepted", "Rejected", "Closed",
    ],
  },
  installStatus: {
    ownerModule: "installer",
    description: "Field execution progress",
    allowedValues: [
      "Not Started", "Scheduled", "En Route",
      "In Progress", "Install Complete",
    ],
  },
  qaStatus: {
    ownerModule: "qa_closeout",
    description: "Post-install quality assurance",
    allowedValues: ["Not Started", "Submitted", "Passed", "Failed"],
  },
  closeoutStatus: {
    ownerModule: "qa_closeout",
    description: "Final project closure state",
    allowedValues: ["Not Started", "QA Passed", "Pending Docs", "Closed"],
  },
  rescissionStatus: {
    ownerModule: "workflow_engine",
    description: "Legal rescission period tracking",
    allowedValues: ["In Rescission", "Cleared for Production"],
  },
  holdStatus: {
    ownerModule: "build_review",
    description: "Administrative hold for review issues",
    allowedValues: ["Sales Action Required", "Info Missing", "On Hold"],
  },
};

/* ═══════════════════════════════════════════
   TRANSITION HELPERS
   ═══════════════════════════════════════════ */

/** Lookup a stage config by its ID */
export function getStageConfig(stageId: JobStage): WorkflowStageConfig | undefined {
  return WORKFLOW_STAGES.find((s) => s.id === stageId);
}

/** Get stages in display order, optionally filtering by enabled */
export function getOrderedStages(enabledOnly = true): WorkflowStageConfig[] {
  return WORKFLOW_STAGES
    .filter((s) => !enabledOnly || s.enabled)
    .sort((a, b) => a.order - b.order);
}

/** Get the next stage(s) from a given stage */
export function getNextStages(currentStage: JobStage): WorkflowStageConfig[] {
  const config = getStageConfig(currentStage);
  if (!config) return [];
  return config.allowedNextStages
    .map(getStageConfig)
    .filter((s): s is WorkflowStageConfig => !!s && s.enabled);
}

/** Get the single primary next stage (first allowed) */
export function getNextStage(currentStage: JobStage): WorkflowStageConfig | null {
  const next = getNextStages(currentStage);
  return next.length > 0 ? next[0] : null;
}

/** Prerequisite validation result */
export interface PrerequisiteResult {
  met: boolean;
  passed: StagePrerequisite[];
  failed: StagePrerequisite[];
  blockers: StagePrerequisite[];
  warnings: StagePrerequisite[];
}

/** Check if a job can enter a target stage */
export function canEnterStage(job: Job, targetStage: JobStage): PrerequisiteResult {
  const config = getStageConfig(targetStage);
  if (!config || !config.enabled) {
    return { met: false, passed: [], failed: [], blockers: [], warnings: [] };
  }

  const passed: StagePrerequisite[] = [];
  const failed: StagePrerequisite[] = [];

  for (const prereq of config.prerequisites) {
    if (prereq.check(job)) {
      passed.push(prereq);
    } else {
      failed.push(prereq);
    }
  }

  const blockers = failed.filter((p) => p.blocking);
  const warnings = failed.filter((p) => !p.blocking);

  return {
    met: blockers.length === 0,
    passed,
    failed,
    blockers,
    warnings,
  };
}

/** Check if a job can advance from its current stage to a target */
export function canAdvanceStage(job: Job, targetStage: JobStage): {
  allowed: boolean;
  reason?: string;
  prerequisiteResult: PrerequisiteResult;
} {
  const currentConfig = getStageConfig(job.currentStage);
  if (!currentConfig) {
    return {
      allowed: false,
      reason: `Unknown current stage: ${job.currentStage}`,
      prerequisiteResult: { met: false, passed: [], failed: [], blockers: [], warnings: [] },
    };
  }

  // Check if transition is allowed
  if (!currentConfig.allowedNextStages.includes(targetStage)) {
    return {
      allowed: false,
      reason: `Cannot transition from "${job.currentStage}" to "${targetStage}"`,
      prerequisiteResult: { met: false, passed: [], failed: [], blockers: [], warnings: [] },
    };
  }

  // Check target stage is enabled
  const targetConfig = getStageConfig(targetStage);
  if (!targetConfig || !targetConfig.enabled) {
    return {
      allowed: false,
      reason: `Stage "${targetStage}" is disabled`,
      prerequisiteResult: { met: false, passed: [], failed: [], blockers: [], warnings: [] },
    };
  }

  // Check prerequisites
  const prereqResult = canEnterStage(job, targetStage);
  return {
    allowed: prereqResult.met,
    reason: prereqResult.met ? undefined : `Prerequisites not met: ${prereqResult.blockers.map((b) => b.label).join(", ")}`,
    prerequisiteResult: prereqResult,
  };
}

/** Check if a role is allowed to act on a job in a given stage */
export function isRoleAuthorized(role: UserRole, stage: JobStage, action: "act" | "view" = "act"): boolean {
  const config = getStageConfig(stage);
  if (!config) return false;
  if (action === "view") return config.viewerRoles.includes(role);
  return config.actorRoles.includes(role);
}

/** Check if a module is allowed to write to a specific status field */
export function canModuleWriteStatus(moduleId: string, statusField: StatusFieldKey): boolean {
  const ownership = STATUS_OWNERSHIP[statusField];
  if (!ownership) return false;
  // workflow_engine can write currentStage from any module context
  if (ownership.ownerModule === "workflow_engine") return true;
  return ownership.ownerModule === moduleId;
}

/** Validate a status value against allowed values */
export function isValidStatusValue(field: StatusFieldKey, value: string): boolean {
  const ownership = STATUS_OWNERSHIP[field];
  if (!ownership) return false;
  return ownership.allowedValues.includes(value);
}

/* ═══════════════════════════════════════════
   SKIP STAGE SUPPORT
   ═══════════════════════════════════════════ */

export interface StageSkipRecord {
  jobId: string;
  skippedStage: JobStage;
  fromStage: JobStage;
  toStage: JobStage;
  skippedBy: string;
  skippedAt: string;
  reason: string;
}

/** Check if a stage can be skipped */
export function canSkipStage(stage: JobStage): boolean {
  const config = getStageConfig(stage);
  return config?.skippable === true;
}

/** Build a skip record for audit trail */
export function buildSkipRecord(
  jobId: string,
  fromStage: JobStage,
  skippedStage: JobStage,
  toStage: JobStage,
  skippedBy: string,
  reason: string,
): StageSkipRecord {
  return {
    jobId,
    skippedStage,
    fromStage,
    toStage,
    skippedBy,
    skippedAt: new Date().toISOString(),
    reason,
  };
}

/* ═══════════════════════════════════════════
   LIFECYCLE SUMMARY (documentation helper)
   ═══════════════════════════════════════════ */

/** Get a full ordered summary of the workflow for documentation/testing */
export function getWorkflowSummary(): Array<{
  stage: string;
  module: string;
  enabled: boolean;
  prerequisites: string[];
  nextStages: string[];
  ownedStatuses: string[];
}> {
  return getOrderedStages(false).map((s) => ({
    stage: s.label,
    module: s.moduleId,
    enabled: s.enabled,
    prerequisites: s.prerequisites.map((p) => p.label),
    nextStages: s.allowedNextStages,
    ownedStatuses: s.ownedStatuses,
  }));
}
